import React, { useState, useMemo } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, RotateCcw } from 'lucide-react';
import EmptyState from './EmptyState';

interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => React.ReactNode;
}

interface Props<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  onRowClick?: (item: T) => void;
  sortable?: boolean;
}

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 rounded bg-surface-200" style={{ width: `${60 + Math.random() * 40}%` }} />
        </td>
      ))}
    </tr>
  );
}

function MobileSkeletonCard({ cols }: { cols: number }) {
  return (
    <div className="animate-pulse rounded-lg border border-surface-200 bg-white p-4">
      {Array.from({ length: Math.min(cols, 4) }).map((_, i) => (
        <div key={i} className="mb-2 last:mb-0">
          <div className="mb-1 h-3 w-16 rounded bg-surface-200" />
          <div className="h-4 w-3/4 rounded bg-surface-200" />
        </div>
      ))}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DataTable<T = any>({
  columns,
  data,
  loading = false,
  error = null,
  emptyMessage = 'No se encontraron registros.',
  onRowClick,
  sortable = true,
}: Props<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string) => {
    if (!sortable) return;
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = (a as any)[sortKey];
      const bVal = (b as any)[sortKey];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let cmp = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal, 'es');
      } else {
        cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sortKey !== columnKey) return <ArrowUpDown className="ml-1 h-3 w-3 text-surface-400" />;
    return sortDir === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3 text-primary-600" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3 text-primary-600" />
    );
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-danger-200 bg-danger-50 p-8 text-center">
        <AlertCircle className="mb-2 h-8 w-8 text-danger-500" />
        <p className="text-sm font-medium text-danger-700">{error}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-danger-100 px-3 py-1.5 text-sm font-medium text-danger-700 hover:bg-danger-200 transition-colors"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reintentar
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <div className="hidden md:block overflow-x-auto rounded-lg border border-surface-200">
          <table className="min-w-full divide-y divide-surface-200">
            <thead className="bg-surface-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-surface-500"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100 bg-white">
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={columns.length} />
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-3 md:hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <MobileSkeletonCard key={i} cols={columns.length} />
          ))}
        </div>
      </>
    );
  }

  if (data.length === 0) {
    return <EmptyState title={emptyMessage} />;
  }

  return (
    <>
      <div className="hidden md:block overflow-x-auto rounded-lg border border-surface-200">
        <table className="min-w-full divide-y divide-surface-200">
          <thead className="bg-surface-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-surface-500 ${
                    sortable && col.sortable ? 'cursor-pointer select-none hover:text-surface-700 transition-colors' : ''
                  }`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <span className="inline-flex items-center">
                    {col.label}
                    {sortable && col.sortable && <SortIcon columnKey={col.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-100 bg-white">
            {sortedData.map((item, idx) => (
              <tr
                key={(item as any).id || idx}
                onClick={() => onRowClick?.(item)}
                className={`transition-colors ${
                  onRowClick ? 'cursor-pointer hover:bg-surface-50' : 'hover:bg-surface-50/50'
                }`}
              >
                {columns.map((col) => (
                  <td key={col.key} className="whitespace-nowrap px-4 py-3 text-sm text-surface-700">
                    {col.render ? col.render(item) : ((item as any)[col.key] as React.ReactNode) ?? '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 md:hidden">
        {sortedData.map((item, idx) => (
          <div
            key={(item as any).id || idx}
            onClick={() => onRowClick?.(item)}
            className={`rounded-lg border border-surface-200 bg-white p-4 transition-colors ${
              onRowClick ? 'cursor-pointer hover:bg-surface-50' : ''
            }`}
          >
            {columns.map((col) => (
              <div key={col.key} className="mb-2 last:mb-0">
                <span className="text-xs font-medium text-surface-400">{col.label}</span>
                <div className="text-sm text-surface-800">
                  {col.render ? col.render(item) : ((item as any)[col.key] as React.ReactNode) ?? '-'}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

export default DataTable;
